import { log } from 'constants/config';
import { AUTOCOMPLETE_CONFIG, CACHE_CONFIG, GEO_FEATURE_FLAGS, logPaidRequest } from './config';

interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class GeoCache {
  private memoryCache: Map<string, CacheItem<any>> = new Map();
  private readonly MAX_CACHE_SIZE = 10000; // Maximum number of entries
  private accessOrder: string[] = []; // Track access order for LRU

  private getCacheKey(type: string, params: any): string {
    return `${type}:${JSON.stringify(params)}`;
  }

  private isExpired(item: CacheItem<any>): boolean {
    return Date.now() - item.timestamp > item.ttl;
  }

  private updateAccessOrder(key: string): void {
    const index = this.accessOrder.indexOf(key);
    if (index > -1) {
      this.accessOrder.splice(index, 1);
    }
    this.accessOrder.push(key);
  }

  private evictLRU(): void {
    while (this.memoryCache.size >= this.MAX_CACHE_SIZE && this.accessOrder.length > 0) {
      const oldestKey = this.accessOrder.shift();
      if (oldestKey) {
        this.memoryCache.delete(oldestKey);
        log('[Cache] Evicted LRU entry:', oldestKey);
      }
    }
  }

  private getFromStorage<T>(key: string): T | null {
    if (!GEO_FEATURE_FLAGS.PERSIST_TO_BROWSER_STORAGE) return null;

    try {
      const stored = localStorage.getItem(`geo_cache_${key}`);
      if (stored) {
        const parsed = JSON.parse(stored) as CacheItem<T>;
        if (!this.isExpired(parsed)) {
          return parsed.data;
        }
        localStorage.removeItem(`geo_cache_${key}`);
      }
    } catch (e) {
      console.error('Cache storage error:', e);
    }
    return null;
  }

  private saveToStorage<T>(key: string, item: CacheItem<T>): void {
    if (!GEO_FEATURE_FLAGS.PERSIST_TO_BROWSER_STORAGE) return;

    try {
      localStorage.setItem(`geo_cache_${key}`, JSON.stringify(item));
    } catch (e) {
      console.error('Cache storage error:', e);
    }
  }

  getAutocomplete(input: string): google.maps.places.AutocompletePrediction[] | null {
    if (!GEO_FEATURE_FLAGS.ENABLE_AUTOCOMPLETE_CACHE) return null;

    const key = this.getCacheKey('autocomplete', { input: input.toLowerCase().trim() });

    // Check memory cache first
    const cached = this.memoryCache.get(key);
    if (cached && !this.isExpired(cached)) {
      console.log('💚 CACHE HIT - SAVED MONEY 💚', {
        type: 'Autocomplete',
        input,
        savedCost: '$0.00283',
        source: 'memory',
      });
      return cached.data;
    }

    // Check localStorage
    const stored = this.getFromStorage<google.maps.places.AutocompletePrediction[]>(key);
    if (stored) {
      console.log('💚 CACHE HIT - SAVED MONEY 💚', {
        type: 'Autocomplete',
        input,
        savedCost: '$0.00283',
        source: 'localStorage',
      });
      // Move to memory cache
      this.evictLRU();
      this.memoryCache.set(key, {
        data: stored,
        timestamp: Date.now(),
        ttl: AUTOCOMPLETE_CONFIG.CACHE_TTL,
      });
      this.updateAccessOrder(key);
      return stored;
    }

    log('[Cache] Autocomplete miss:', input);
    return null;
  }

  setAutocomplete(input: string, predictions: google.maps.places.AutocompletePrediction[]): void {
    if (!GEO_FEATURE_FLAGS.ENABLE_AUTOCOMPLETE_CACHE) return;

    log('[Cache] Storing autocomplete for:', input, 'predictions:', predictions.length);

    const key = this.getCacheKey('autocomplete', { input: input.toLowerCase().trim() });
    const item = {
      data: predictions,
      timestamp: Date.now(),
      ttl: AUTOCOMPLETE_CONFIG.CACHE_TTL,
    };

    this.evictLRU();
    this.memoryCache.set(key, item);
    this.updateAccessOrder(key);
    this.saveToStorage(key, item);
  }

  getPlaceDetails(placeId: string): google.maps.places.PlaceResult | null {
    if (!GEO_FEATURE_FLAGS.ENABLE_PLACE_DETAILS_CACHE) return null;

    const key = this.getCacheKey('place', { placeId });
    const cached = this.memoryCache.get(key);

    if (cached && !this.isExpired(cached)) {
      console.log('💚 CACHE HIT - SAVED MONEY 💚', {
        type: 'Place Details',
        placeId,
        savedCost: '$0.017',
        source: 'memory',
      });
      this.updateAccessOrder(key);
      return cached.data;
    }

    const stored = this.getFromStorage<google.maps.places.PlaceResult>(key);
    if (stored) {
      console.log('💚 CACHE HIT - SAVED MONEY 💚', {
        type: 'Place Details',
        placeId,
        savedCost: '$0.017',
        source: 'localStorage',
      });
      this.evictLRU();
      this.memoryCache.set(key, {
        data: stored,
        timestamp: Date.now(),
        ttl: CACHE_CONFIG.PLACE_DETAILS_TTL,
      });
      this.updateAccessOrder(key);
      return stored;
    }

    return null;
  }

  setPlaceDetails(placeId: string, place: google.maps.places.PlaceResult): void {
    if (!GEO_FEATURE_FLAGS.ENABLE_PLACE_DETAILS_CACHE) return;

    const key = this.getCacheKey('place', { placeId });
    const item = {
      data: place,
      timestamp: Date.now(),
      ttl: CACHE_CONFIG.PLACE_DETAILS_TTL,
    };

    this.evictLRU();
    this.memoryCache.set(key, item);
    this.updateAccessOrder(key);
    this.saveToStorage(key, item);
  }

  getGeocoding(lat: number, lng: number): string | null {
    if (!GEO_FEATURE_FLAGS.ENABLE_GEOCODING_CACHE) return null;

    const key = this.getCacheKey('geocode', { lat: lat.toFixed(6), lng: lng.toFixed(6) });
    const cached = this.memoryCache.get(key);

    if (cached && !this.isExpired(cached)) {
      console.log('💚 CACHE HIT - SAVED MONEY 💚', {
        type: 'Geocoding',
        coordinates: { lat, lng },
        savedCost: '$0.005',
        source: 'memory',
      });
      this.updateAccessOrder(key);
      return cached.data;
    }

    const stored = this.getFromStorage<string>(key);
    if (stored) {
      console.log('💚 CACHE HIT - SAVED MONEY 💚', {
        type: 'Geocoding',
        coordinates: { lat, lng },
        savedCost: '$0.005',
        source: 'localStorage',
      });
      this.evictLRU();
      this.memoryCache.set(key, {
        data: stored,
        timestamp: Date.now(),
        ttl: CACHE_CONFIG.GEOCODING_TTL,
      });
      this.updateAccessOrder(key);
      return stored;
    }

    return null;
  }

  setGeocoding(lat: number, lng: number, address: string): void {
    if (!GEO_FEATURE_FLAGS.ENABLE_GEOCODING_CACHE) return;

    const key = this.getCacheKey('geocode', { lat: lat.toFixed(6), lng: lng.toFixed(6) });
    const item = {
      data: address,
      timestamp: Date.now(),
      ttl: CACHE_CONFIG.GEOCODING_TTL,
    };

    this.evictLRU();
    this.memoryCache.set(key, item);
    this.updateAccessOrder(key);
    this.saveToStorage(key, item);
  }

  // New methods for address-to-coordinates caching
  getGeocodingByAddress(address: string): { latitude: number; longitude: number } | null {
    if (!GEO_FEATURE_FLAGS.ENABLE_GEOCODING_CACHE) return null;

    const key = this.getCacheKey('geocode_address', { address: address.toLowerCase().trim() });
    const cached = this.memoryCache.get(key);

    if (cached && !this.isExpired(cached)) {
      console.log('💚 CACHE HIT - SAVED MONEY 💚', {
        type: 'Geocoding by Address',
        address,
        savedCost: '$0.005',
        source: 'memory',
      });
      this.updateAccessOrder(key);
      return cached.data;
    }

    const stored = this.getFromStorage<{ latitude: number; longitude: number }>(key);
    if (stored) {
      console.log('💚 CACHE HIT - SAVED MONEY 💚', {
        type: 'Geocoding by Address',
        address,
        savedCost: '$0.005',
        source: 'localStorage',
      });
      this.evictLRU();
      this.memoryCache.set(key, {
        data: stored,
        timestamp: Date.now(),
        ttl: CACHE_CONFIG.GEOCODING_TTL,
      });
      this.updateAccessOrder(key);
      return stored;
    }

    return null;
  }

  setGeocodingByAddress(address: string, lat: number, lng: number): void {
    if (!GEO_FEATURE_FLAGS.ENABLE_GEOCODING_CACHE) return;

    const key = this.getCacheKey('geocode_address', { address: address.toLowerCase().trim() });
    const item = {
      data: { latitude: lat, longitude: lng },
      timestamp: Date.now(),
      ttl: CACHE_CONFIG.GEOCODING_TTL,
    };

    this.evictLRU();
    this.memoryCache.set(key, item);
    this.updateAccessOrder(key);
    this.saveToStorage(key, item);
  }

  cleanup(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    for (const [key, item] of this.memoryCache.entries()) {
      if (this.isExpired(item)) {
        keysToDelete.push(key);
      }
    }

    // Remove expired entries
    keysToDelete.forEach((key) => {
      this.memoryCache.delete(key);
      const index = this.accessOrder.indexOf(key);
      if (index > -1) {
        this.accessOrder.splice(index, 1);
      }
    });
  }

  getStats() {
    return {
      size: this.memoryCache.size,
      enabled: {
        autocomplete: GEO_FEATURE_FLAGS.ENABLE_AUTOCOMPLETE_CACHE,
        placeDetails: GEO_FEATURE_FLAGS.ENABLE_PLACE_DETAILS_CACHE,
        geocoding: GEO_FEATURE_FLAGS.ENABLE_GEOCODING_CACHE,
      },
    };
  }
}

export const geoCache = new GeoCache();

// Clean up cache periodically
if (typeof window !== 'undefined') {
  setInterval(() => geoCache.cleanup(), 10 * 60 * 1000);
}
